import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface WizardUIContextValue {
  sidebarCollapsed: boolean;
  helpDrawerExpanded: boolean;
  toggleSidebar: () => void;
  /** Erzwingt den eingeklappten Zustand, ohne zu togglen (für breitenabhängiges Auto-Collapse). */
  collapseSidebar: () => void;
  toggleHelpDrawer: () => void;
  /** Erzwingt den geschlossenen Zustand, ohne zu togglen (für breitenabhängiges Auto-Collapse). */
  closeHelpDrawer: () => void;
  /** Öffnet den Hilfebereich, ohne zu togglen (für die Hilfe-Buttons in den Formularen). */
  openHelp: () => void;

  activeVideoId: string | null;
  showVideoModal: (videoId: string) => void;
  closeVideoModal: () => void;

  showSchnittstellenModal: boolean;
  openSchnittstellenModal: () => void;
  closeSchnittstellenModal: () => void;
}

const WizardUIContext = createContext<WizardUIContextValue | null>(null);

export function WizardUIProvider({ children }: { children: ReactNode }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [helpDrawerExpanded, setHelpDrawerExpanded] = useState(false);
  const [activeVideoId, setActiveVideoId] = useState<string | null>(null);
  const [showSchnittstellenModal, setShowSchnittstellenModal] = useState(false);

  const toggleSidebar = useCallback(() => setSidebarCollapsed((prev) => !prev), []);
  const collapseSidebar = useCallback(() => setSidebarCollapsed(true), []);
  const toggleHelpDrawer = useCallback(() => setHelpDrawerExpanded((prev) => !prev), []);
  const closeHelpDrawer = useCallback(() => setHelpDrawerExpanded(false), []);
  const openHelp = useCallback(() => setHelpDrawerExpanded(true), []);
  const showVideoModal = useCallback((videoId: string) => setActiveVideoId(videoId), []);
  const closeVideoModal = useCallback(() => setActiveVideoId(null), []);
  const openSchnittstellenModal = useCallback(() => setShowSchnittstellenModal(true), []);
  const closeSchnittstellenModal = useCallback(() => setShowSchnittstellenModal(false), []);

  const value = useMemo(
    () => ({
      sidebarCollapsed,
      helpDrawerExpanded,
      toggleSidebar,
      collapseSidebar,
      toggleHelpDrawer,
      closeHelpDrawer,
      openHelp,
      activeVideoId,
      showVideoModal,
      closeVideoModal,
      showSchnittstellenModal,
      openSchnittstellenModal,
      closeSchnittstellenModal
    }),
    [
      sidebarCollapsed,
      helpDrawerExpanded,
      toggleSidebar,
      collapseSidebar,
      toggleHelpDrawer,
      closeHelpDrawer,
      openHelp,
      activeVideoId,
      showVideoModal,
      closeVideoModal,
      showSchnittstellenModal,
      openSchnittstellenModal,
      closeSchnittstellenModal
    ]
  );

  return <WizardUIContext.Provider value={value}>{children}</WizardUIContext.Provider>;
}

export function useWizardUI(): WizardUIContextValue {
  const context = useContext(WizardUIContext);
  if (!context) {
    throw new Error("useWizardUI() muss innerhalb von <WizardUIProvider> aufgerufen werden.");
  }
  return context;
}
